/** Which rule set a match runs under. Only `versus` is strictly cosmetic-only. */
/**
 * versus: turn-based teams. brawl: the same teams, all at once in real time (no turns, cooldowns
 * instead). coop: turns against AI waves. campaign: turns through hand-tuned levels.
 */
export type GameMode = 'versus' | 'brawl' | 'coop' | 'campaign';
export const GAME_MODES: readonly GameMode[] = ['versus', 'brawl', 'coop', 'campaign'];

/** Player versus player: Versus and Brawl. Cosmetics only, teams from the lobby. */
export function isPvpMode(mode: GameMode): boolean {
  return mode === 'versus' || mode === 'brawl';
}

/** Co-op Siege and Solo Campaign allow gameplay perks; Versus never does. */
export function isPveMode(mode: GameMode): boolean {
  return mode === 'coop' || mode === 'campaign';
}

/**
 * Everything a team can stock, Territory War style. Attacks: a thrown ball (Raft Wars' tennis
 * ball), a grenade, a big grenade, a cluster bomb, a mine, a pistol, a shotgun, a sniper rifle, a
 * harpoon gun, the boot (a kick), a bat, a mallet, a pike, a mortar (a lobbed shell), an RPG
 * (fast and flat), an SMG (a burst), a sticky bomb, a rubber bomb (bouncy), an airstrike, a massive bomb,
 * a crossbow, a railgun (through everything), a TNT charge set down within reach, and the
 * catapult (a map emplacement on the canyon maps: free boulders for whoever stands beside it).
 * Gear, which takes the turn's move instead of ending the turn: a grapple hook, a platform or a
 * long bridge set down where you point, a teleport, a sandbag wall, a medkit, a raft repair kit
 * and a pickaxe that digs a tunnel. Ball, pistol, boot and catapult are free; the rest is bought
 * in the buy phase.
 */
export type WeaponId =
  | 'ball' | 'grenade' | 'bignade' | 'cluster' | 'mine' | 'gun' | 'shotgun' | 'sniper' | 'harpoon' | 'boot' | 'bat' | 'mallet' | 'pike'
  | 'mortar' | 'rpg' | 'smg' | 'sticky' | 'rubber' | 'airstrike' | 'massive' | 'crossbow' | 'railgun' | 'tnt' | 'catapult'
  | 'grapple' | 'platform' | 'bridge' | 'teleport' | 'sandbags' | 'medkit' | 'repair' | 'pickaxe' | 'tunneler';

export type MatchResult = 'win' | 'loss' | 'draw';

/** How the actor relates to the target of a hit or kill. Only `enemy` earns XP. */
export type Relation = 'enemy' | 'friendly' | 'self';

export type KillCause = 'damage' | 'drown' | 'fall';

/**
 * Events emitted by the match simulation. The XP engine consumes them, and the
 * results screen shows them back to the player as a breakdown. All ids are player ids.
 */
export type MatchEvent =
  | {
      type: 'hit';
      /** Groups everything one attack caused, so multi-kills can be detected. */
      shotId: string;
      actor: string;
      target: string;
      damage: number;
      headshot: boolean;
      /** Projectile touched the unit before touching terrain (or a gun / boot hit). */
      direct: boolean;
      /** Horizontal distance travelled as a fraction of level width (0..1). */
      distanceFraction: number;
      weapon: WeaponId;
      relation: Relation;
    }
  | {
      type: 'kill';
      shotId: string;
      actor: string;
      target: string;
      cause: KillCause;
      relation: Relation;
    }
  | { type: 'turnPlayed'; actor: string };

export interface CampaignOutcome {
  levelId: string;
  /** 0 = failed, 1 = cleared, 2 = within par, 3 = flawless. */
  stars: 0 | 1 | 2 | 3;
  /** Ground War only: a campaign win on Hard (stored as `gwh:<levelId>`). */
  crown?: true;
}

/** Per-player summary the simulation produces when a match ends. */
export interface PlayerOutcome {
  playerId: string;
  result: MatchResult;
  unitsStart: number;
  unitsAlive: number;
  hpStart: number;
  hpRemaining: number;
  roundsPlayed: number;
  parRounds: number;
  /** True if the player was ever down to a single unit under 25% HP. */
  lastStand: boolean;
  campaign?: CampaignOutcome;
}

/** Lifetime counters. Challenge cosmetics are gated on these. */
export interface PlayerStats {
  matches: number;
  wins: number;
  flawlessWins: number;
  kills: number;
  hits: number;
  headshots: number;
  directHits: number;
  longShots: number;
  drownKills: number;
  /** Best star rating per campaign level id. */
  campaignStars: Record<string, number>;
  /** ISO date (YYYY-MM-DD) of the last win that paid the daily bonus. */
  lastWinDay?: string;
  /** Ground War campaign tree: node id -> level 1..max (absent = 0). Written by setGwTree. */
  gwTree?: Record<string, number>;
  /** Ground War class grades per class id (absent = base grades). Written by setGwClasses. */
  gwClasses?: Record<string, { weapon: number; vitality: number; swiftness: number; mending: number }>;
}

export type StatKey = Exclude<keyof PlayerStats, 'campaignStars' | 'lastWinDay' | 'gwTree' | 'gwClasses'>;

export function emptyStats(): PlayerStats {
  return {
    matches: 0,
    wins: 0,
    flawlessWins: 0,
    kills: 0,
    hits: 0,
    headshots: 0,
    directHits: 0,
    longShots: 0,
    drownKills: 0,
    campaignStars: {},
  };
}
